import React, { useState } from 'react';
import {
    Popover,
    PopoverTrigger,
    PopoverSurface,
    makeStyles,
    tokens,
    Text,
    Body2,
    Caption1,
} from '@fluentui/react-components';

const useStyles = makeStyles({
    container: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalXS,
    },
    label: {
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase200,
    },
    text: {
        color: tokens.colorBrandForeground1,
        fontSize: tokens.fontSizeBase200,
        cursor: 'pointer',
        textDecoration: 'underline',
        '&:hover': {
            color: tokens.colorBrandForeground2,
        },
    },
    popover: {
        maxWidth: '400px',
        maxHeight: '300px',
        overflowY: 'auto',
        padding: tokens.spacingVerticalM,
    },
    productList: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXS,
    },
    productItem: {
        padding: tokens.spacingVerticalXS,
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: tokens.borderRadiusSmall,
        wordBreak: 'break-word',
    },
    header: {
        marginBottom: tokens.spacingVerticalS,
        fontWeight: tokens.fontWeightSemibold,
    },
    emptyState: {
        fontStyle: 'italic',
        color: tokens.colorNeutralForeground3,
    },
});

interface ProductNamesBadgeProps {
    productNames?: string;
}

const ProductNamesBadge: React.FC<ProductNamesBadgeProps> = ({ productNames }) => {
    const classes = useStyles();
    const [isOpen, setIsOpen] = useState(false);

    // Parse the pipe-delimited string into an array
    const parseProductNames = (names?: string): string[] => {
        if (!names || names.trim() === '') return [];
        return names.split('|').map(name => name.trim()).filter(name => name.length > 0);
    };

    const products = parseProductNames(productNames);
    const productCount = products.length;

    // Don't render anything if there are no products
    if (productCount === 0) {
        return null;
    }

    return (
        <Popover
            open={isOpen}
            onOpenChange={(_, data) => setIsOpen(data.open)}
            positioning="below-start"
        >
            <div className={classes.container}>
                <Caption1 className={classes.label}>Products:</Caption1>
                <PopoverTrigger disableButtonEnhancement>
                    <Text className={classes.text}>
                        {productCount}
                    </Text>
                </PopoverTrigger>
            </div>
            <PopoverSurface className={classes.popover}>
                <div>
                    <Text className={classes.header}>
                        {productCount} Product{productCount === 1 ? '' : 's'}
                    </Text>
                    <div className={classes.productList}>
                        {products.length > 0 ? (
                            products.map((product, index) => (
                                <div key={index} className={classes.productItem}>
                                    <Body2>{product}</Body2>
                                </div>
                            ))
                        ) : (
                            <Caption1 className={classes.emptyState}>
                                No products available
                            </Caption1>
                        )}
                    </div>
                </div>
            </PopoverSurface>
        </Popover>
    );
};

export default ProductNamesBadge;